import { useEffect } from "react";
import { gql } from "@apollo/client";
import { DetailHero, SEO } from "@components";
import { ExhibitQuery } from "@graphqlTypes";
import { FaustTemplate } from "@faustwp/core";
import { HIGHLIGHT_COLORS } from "@/core/constants";
import { ExhibitDetailFragment } from "@/fragments/Exhibit";
import { usePageHighlight } from "@/providers/PageHighlightContext";
import { FlexiblePageContentFragment } from "@/fragments/FlexibleContent";
import { FlexibleContentMap } from "@/components/ComponentMaps";
import { getBasicQueryVars, getPageTitle } from "@/core/utils";
import { usePageLoading } from "@/providers/LoadingContext";
import { useTrackRouteChange } from "@/providers/TrackingContext";
import { usePageWrapperData } from "@/providers/PageWrapperContext";

const Exhibit: FaustTemplate<ExhibitQuery> = ({ data, loading, pageWrapperData }: FaustPageProps<ExhibitQuery>) => {
  const { setHighlightColor } = usePageHighlight();
  const { previewLoading, setPreviewLoading } = usePageLoading();
  const { siteTitle } = usePageWrapperData();
  const { recentStories } = pageWrapperData;
  const {
    detailHero,
    title,
    flexiblePageContent,
    pageData: {
      ogTitle,
      ogDescription,
      featuredImage,
      pageCategory,
    } = {},
    exhibitData: {
      ticketLink,
      customCursorText,
    } = {}
  } = data?.rhExhibit || {};
  const [category] = pageCategory || [];
  const seoTitle = getPageTitle(siteTitle, ogTitle || title);

  useEffect(() => {
    setHighlightColor(HIGHLIGHT_COLORS.BLUE);
  }, [category]);
  useTrackRouteChange(category?.slug);

  useEffect(() => {
    if (!loading && previewLoading) setPreviewLoading(false);
  }, [loading, previewLoading]);

  if (loading) return;
  return (
    <>
      <SEO title={seoTitle} description={ogDescription} image={featuredImage} />
      <DetailHero
        page={{ node: { ...data.rhExhibit, __typename: 'Exhibit' } }}
        headline={title}
        heroLink={ticketLink}
        cursorText={customCursorText}
        {...detailHero}
      />
      { flexiblePageContent?.content && (
        <FlexibleContentMap content={flexiblePageContent.content} recentStories={recentStories} />
      )}
    </>
  );
};

Exhibit.query = gql`
  ${FlexiblePageContentFragment}
  ${ExhibitDetailFragment}
  query Exhibit($databaseId: ID!, $asPreview: Boolean = false, $language: String!) {
    rhExhibit(id: $databaseId, asPreview: $asPreview, language: $language) {
      ...ExhibitDetailFragment
      flexiblePageContent {
        ...ContentFragment
      }
    }
  }
`;

Exhibit.variables = getBasicQueryVars;

export default Exhibit;
